"use client";

import { useEffect, useRef } from "react";

const PDF_URL = "/Our Past Work - Zoot.pdf";
const PDF_JS_SRC = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js";
const PDF_WORKER_SRC = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";

type Nullable<T> = T | null;

type PDFRenderTask = {
  promise: Promise<void>;
};

type PDFPageViewport = {
  width: number;
  height: number;
};

type PDFRenderContext = {
  canvasContext: CanvasRenderingContext2D;
  viewport: PDFPageViewport;
};

type PDFPageProxy = {
  getViewport: (options: { scale: number }) => PDFPageViewport;
  render: (context: PDFRenderContext) => PDFRenderTask;
};

type PDFDocumentProxy = {
  numPages: number;
  getPage: (pageNumber: number) => Promise<PDFPageProxy>;
  destroy?: () => void;
};

type PDFJSStatic = {
  GlobalWorkerOptions: {
    workerSrc: string;
  };
  getDocument: (src: string) => {
    promise: Promise<PDFDocumentProxy>;
  };
};

declare global {
  interface Window {
    pdfjsLib?: PDFJSStatic;
  }
}

const loadPdfJsScript = () =>
  new Promise<void>((resolve, reject) => {
    if (typeof window === "undefined") {
      resolve();
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      "script[data-pdfjs-lib]"
    );

    if (existing) {
      if (window.pdfjsLib) {
        resolve();
      } else {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Failed to load pdf.js")), {
          once: true,
        });
      }
      return;
    }

    const script = document.createElement("script");
    script.src = PDF_JS_SRC;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.referrerPolicy = "no-referrer";
    script.dataset.pdfjsLib = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load pdf.js"));
    document.head.appendChild(script);
  });

const debounce = <T extends (...args: never[]) => void>(fn: T, delay = 200) => {
  let timeoutId: number;
  return (...args: Parameters<T>) => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => fn(...args), delay);
  };
};

export default function OurPastWorkViewer() {
  const containerRef = useRef<Nullable<HTMLDivElement>>(null);
  const loaderRef = useRef<Nullable<HTMLDivElement>>(null);

  useEffect(() => {
    let cancelled = false;
    let loaderDelayHandle: number | null = null;
    let resizeHandler: ((this: Window, ev: UIEvent) => void) | null = null;
    let pdfDoc: PDFDocumentProxy | null = null;

    const container = containerRef.current;
    const loader = loaderRef.current;

    if (!container || !loader) {
      return () => undefined;
    }

    const showLoader = () => {
      loader.style.display = "grid";
      loader.textContent = "Loading document…";
      loader.removeAttribute("aria-hidden");
      loader.setAttribute("aria-busy", "true");
    };

    const clearLoaderDelay = () => {
      if (loaderDelayHandle !== null) {
        clearTimeout(loaderDelayHandle);
        loaderDelayHandle = null;
      }
    };

    const hideLoader = () => {
      clearLoaderDelay();
      loader.style.display = "none";
      loader.setAttribute("aria-hidden", "true");
      loader.removeAttribute("aria-busy");
    };

    const renderAllPages = async () => {
      if (!pdfDoc || !container || cancelled) return;

      container.innerHTML = "";

      const containerWidth = container.clientWidth || window.innerWidth;

      for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum += 1) {
        if (cancelled) break;

        const page = await pdfDoc.getPage(pageNum);
        const initialViewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / initialViewport.width;
        const scaledViewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        canvas.className = "page-canvas";
        canvas.style.width = `${scaledViewport.width}px`;
        canvas.style.height = `${scaledViewport.height}px`;

        const outputScale = window.devicePixelRatio || 1;
        canvas.width = Math.floor(scaledViewport.width * outputScale);
        canvas.height = Math.floor(scaledViewport.height * outputScale);

        const context = canvas.getContext("2d", { alpha: false });
        if (!context) continue;

        const renderViewport = page.getViewport({ scale: scale * outputScale });

        container.appendChild(canvas);

        const renderContext: PDFRenderContext = {
          canvasContext: context,
          viewport: renderViewport,
        };

        await page
          .render(renderContext)
          .promise.catch((error: Error) => {
            if (!cancelled) {
              console.error("Error rendering PDF page", error);
            }
          });
      }
    };

    const handleResize = debounce(() => {
      renderAllPages().catch((error) => {
        if (!cancelled) {
          console.error("Error re-rendering PDF on resize", error);
        }
      });
    }, 250);

    const loadPdf = async () => {
      try {
        await loadPdfJsScript();
        const pdfjsLib = window.pdfjsLib;

        if (!pdfjsLib) {
          throw new Error("pdfjsLib is unavailable on window");
        }

        pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;

        pdfDoc = await pdfjsLib.getDocument(PDF_URL).promise;
        if (cancelled) return;

        hideLoader();
        await renderAllPages();

        resizeHandler = handleResize;
        window.addEventListener("resize", resizeHandler, { passive: true });
      } catch (error) {
        console.error("Error loading PDF", error);
        if (loader) {
          clearLoaderDelay();
          loader.style.display = "grid";
          loader.textContent = "Sorry, the document failed to load.";
          loader.removeAttribute("aria-hidden");
          loader.setAttribute("aria-busy", "false");
        }
      }
    };

    loader.style.display = "none";
    loaderDelayHandle = window.setTimeout(showLoader, 200);

    loadPdf();

    return () => {
      cancelled = true;

      if (loaderDelayHandle) {
        clearTimeout(loaderDelayHandle);
        loaderDelayHandle = null;
      }

      if (resizeHandler) {
        window.removeEventListener("resize", resizeHandler);
      }

      if (container) {
        container.innerHTML = "";
      }

      pdfDoc?.destroy?.();
    };
  }, []);

  return (
    <div className="viewer-root">
      <main className="viewer-main">
        <h1 className="sr-only">Our Past Work - Zoot PDF Viewer</h1>
        <div className="pdf-wrapper" aria-live="polite">
          <div ref={loaderRef} id="loader" aria-hidden="true"></div>
          <div ref={containerRef} id="pdf-container"></div>
        </div>
      </main>

      <style jsx>{`
        .viewer-root {
          min-height: 100vh;
          background: #000;
          color: #f8fafc;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI",
            sans-serif;
        }

        .viewer-main {
          min-height: 100vh;
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: clamp(1rem, 3vw, 2.5rem) clamp(0.75rem, 4vw, 3rem);
          background: #000;
          width: 100%;
        }

        .pdf-wrapper {
          width: min(100%, 92vw);
          max-width: 1440px;
          display: grid;
          gap: clamp(0.75rem, 2vw, 1.5rem);
        }

        #loader {
          display: grid;
          place-items: center;
          min-height: 40vh;
          color: rgba(226, 232, 240, 0.85);
          font-size: clamp(1rem, 2vw, 1.25rem);
          letter-spacing: 0.04em;
        }

        #pdf-container {
          width: 100%;
          display: grid;
          gap: clamp(0.75rem, 2vw, 1.5rem);
          padding: clamp(0.25rem, 1.5vw, 1rem) 0;
        }

        .page-canvas {
          width: 100%;
          height: auto;
          max-width: 100%;
          margin: 0 auto;
          box-shadow: 0 28px 60px rgba(0, 0, 0, 0.55);
          border-radius: clamp(0.5rem, 1vw, 0.75rem);
          overflow: hidden;
          background: #f8fafc;
        }

        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          border: 0;
        }

        @media (max-width: 640px) {
          .viewer-main {
            padding: clamp(0.75rem, 4vw, 1.5rem);
          }

          #pdf-container {
            gap: clamp(0.5rem, 3vw, 1rem);
          }

          .page-canvas {
            box-shadow: 0 18px 36px rgba(0, 0, 0, 0.55);
          }
        }

        @media (min-width: 1280px) {
          .viewer-main {
            padding-left: clamp(2rem, 6vw, 6rem);
            padding-right: clamp(2rem, 6vw, 6rem);
          }
        }
      `}</style>
    </div>
  );
}
